import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { CalendarSidebar } from "./CalendarSidebar";
import {
	GlobalNavigationCriteria,
	useGlobalNavigationStore
} from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import { AppType } from "client/Api/Apps/common";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useSynchronizedRef,
	useValueEqualMemo,
	useUserTimezone,
	useWorkspaceMediaQuery,
	useLocalStorageState,
	useUserScopedKey,
	useUnmountedCancellation
} from "client/Hooks";
import { tryParseDateOnly } from "client/Misc/parseUtils";
import { CalendarSidebarContent } from "./CalendarSidebarContent/CalendarSidebarContent";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	CalendarColor,
	CalendarKind,
	getViewModel,
	enableCalendar,
	disableCalendar,
	CalendarContentViewKind,
	CalendarSaveResultModel,
	AppointmentResponseStatus,
	AppointmentDetailModel,
	RecurringAppointmentOperationKind,
	CalendarInfoModel,
	deleteContactCalendar
} from "client/Api/Calendar/calendar";
import {
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import {
	CalendarContent,
	CalendarContentHandler
} from "./CalendarContent/CalendarContent";
import { never } from "client_legacy/ndaw/errors";
import { partition, sortBy } from "client_legacy/ndaw/arrayHelpers";
import { CalendarClientPreference, userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	CalendarHighlightDateOnlyRange,
	FloatingActionButton,
	useConfirmationDialog
} from "client/Components";
import {
	navigateToOwaCalendar
} from "../Email/OwaNavigation/owaNavigator";
import { EmailNotConfiguredDialog } from "../Email/Dialogs/EmailNotConfiguredDialog";
import { usePageTitle } from "client/Hooks/usePageTitle";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	addDays,
	convertToDateOnly,
	generateDateArray,
	getNextHalfOrWholeHour,
	getStartOfWeek,
	convertToDateTime as convertDateOnlyToDateTime
} from "client/DateAndTime/dateUtils";
import { convertToDateTime, convertToTimeOnly } from "client/DateAndTime/timeUtils";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	connectToRouter,
	route
} from "client/Navigation";
import { OnlineMeeting } from "client/Api/Notifications/notifications";
import { setHttpError } from "../ErrorPage/errorStore";
import {
	ensureNotNil,
	getNumericEnumValues,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { MINUTES_IN_HOUR } from "./CalendarContent/Events/DayColumn/TimeIndicator";
import { WorkingDaysModel } from "client/Api/UserProfile/service";
import { generateWorkWeekDateArray } from "./generateWorkWeekDateArray";
import { HttpRequestError } from "client/Api/httpClient";
import { ExchangeAuthenticationErrorDialog } from "../Email/Dialogs/ExchangeAuthenticationErrorDialog";
import { PageFailedDialog } from "../Email/Dialogs/PageFailedDialog";
import { useAnalyticsStore } from "../Analytics/analyticsStore";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { CalendarForAppointmentLoading, useAppointmentLoading } from "./useAppointmentLoading";
import { useCalendarEvents } from "./hooks";
import { ComposeAppointmentDialog } from "./ComposeAppointmentDialog/ComposeAppointmentDialog";
import { ComposeAppointmentBox, ComposeAppointmentBoxInitialData } from "./CalendarContent/ComposeAppointmentBox/ComposeAppointmentBox";
import { Coordinate } from "client/Misc/commonTypes";
import styles from "./CalendarPage.module.less";
import {
	CalendarAppointmentFormModel,
	CalendarItem,
	DisabledCalendarItem,
	EnabledCalendarItem
} from "./common";
import { useComposeAppointmentDialog } from "./AddCalendarDialog";
import {
	ExchangeConfigurationErrorKind,
	ExchangeConfigurationErrorResponseModel
} from "client/Api/Email/email";

export type CalendarPageProps = {
	date?: DateOnly;
	onNavigate: NavigationCallback;
};

export const CalendarPage = (
	props: CalendarPageProps
): ReactElement => {
	const {
		date: dateFromUrl,
		onNavigate
	} = props;

	const [calendars, setCalendars] = useState<readonly CalendarItem[]>([]);
	const [workingDays, setWorkingDays] = useState<WorkingDaysModel>(UNSET_WORKING_DAYS);
	const [updateInterval, setUpdateInterval] = useState<number | null>(null);
	const [loadingCalendarsState, setLoadingCalendarsState] = useState<LoadingCalendarsState>({
		isLoading: false,
		hasError: false
	});

	const [isDeletingCalendar, setIsDeletingCalendar] = useState<boolean>(false);
	const [appointments, setAppointments] = useState<readonly CalendarAppointment[]>([]);
	const [composeAppointmentDialogData, setComposeAppointmentDialogData] =
		useState<ComposeAppointmentDialogInfoData>({ isOpen: false });
	const [composeAppointmentBoxLocationAndData, setComposeAppointmentBoxLocationAndData] = useState<ComposeAppointmentBoxLocationAndData | null>(null);
	const localStorageKey: string = useUserScopedKey("calendar-content-view");
	const [view, setView] = useLocalStorageState<CalendarContentViewKind>(localStorageKey, CALENDAR_DEFAULT_VIEW);

	usePageTitle(localize("_Calendar"));
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const calendarContentRef: RefObject<CalendarContentHandler | null> = useRef(null);
	const pageRef: RefObject<HTMLDivElement | null> = useRef(null);
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);
	const setActiveGlobalNavigationCriteria: (criteria: GlobalNavigationCriteria) => () => void = useGlobalNavigationStore(
		x => x.setActiveCriteria
	);
	const userTimezone: TimeZone = useUserTimezone();

	const [addCalendarDialog, openAddCalendarDialog]= useComposeAppointmentDialog();
	const [confirmationDialog, openConfirmationDialog]= useConfirmationDialog();

	useCalendarEvents(setAppointments);

	const {
		loadingStatusMap,
		resetAppointmentLoading,
		enqueueAppointmentRetrieval,
		cancelAppointmentRetrieval
	} = useAppointmentLoading(
		setAppointments
	);

	const {
		isExchangeConfigured,
		isExchangeEnabled
	} = useMemo(
		(): {
			isExchangeConfigured: boolean;
			isExchangeEnabled: boolean;
		} => {
			const {
				exchange: {
					isConfigured: isExchangeConfigured,
					isEnabled: isExchangeEnabled
				}
			} = userConfiguration();

			return {
				isExchangeConfigured,
				isExchangeEnabled
			};
		},
		[]
	);

	usePageTitle(localize("_Calendar"));

	const viewToRender = useMemo(
		() => isMobile
			? CalendarContentViewKind.Day
			: view,
		[
			isMobile,
			view
		]
	);

	const selectedDate = useMemo(
		(): DateOnly => {
			return dateFromUrl ?? convertToDateOnly(new Date(), userTimezone);
		},
		[dateFromUrl, userTimezone]
	);

	const calculateHighlightRangeForDate = useCallback(
		(date: DateOnly): CalendarHighlightDateOnlyRange => {
			const dayCount: number = getDayCountOfViewKind(viewToRender);

			const start: DateOnly = viewToRender === CalendarContentViewKind.Week
				|| viewToRender === CalendarContentViewKind.WorkWeek
					? getStartOfWeek(date)
					: date;

			const end: DateOnly = addDays(start, dayCount);

			return {
				start,
				end
			};
		},
		[viewToRender]
	);

	const dateRange = useMemo(
		(): readonly DateOnly[] => {
			switch(viewToRender) {
				case CalendarContentViewKind.Day:
					return [selectedDate];
				case CalendarContentViewKind.Week:
				case CalendarContentViewKind.WorkWeek:
					const firstWeekDay: DateOnly = getStartOfWeek(selectedDate);

					return generateDateArray(
						firstWeekDay,
						getDayCountOfViewKind(viewToRender)
					);
				case CalendarContentViewKind.FiveDays:
					return generateDateArray(
						selectedDate,
						getDayCountOfViewKind(viewToRender)
					);
				case CalendarContentViewKind.ThreeDays:
					return generateDateArray(
						selectedDate,
						getDayCountOfViewKind(viewToRender)
					);
				default:
					never(viewToRender);
			}
		},
		[
			selectedDate,
			viewToRender
		]
	);

	const dateRangeToFetchAppointments = useValueEqualMemo(
		(): readonly DateOnly[] => dateRange,
		[dateRange]
	);

	const dateRangeToRender = useMemo(
		(): CalendarDateRange => {
			let toRender: readonly DateOnly[];

			if (viewToRender === CalendarContentViewKind.WorkWeek) {
				toRender = generateWorkWeekDateArray(
					dateRangeToFetchAppointments,
					workingDays
				);
			} else {
				toRender = dateRangeToFetchAppointments;
			}

			return {
				value: selectedDate,
				toRender
			};
		},
		[
			dateRangeToFetchAppointments,
			selectedDate,
			viewToRender,
			workingDays
		]
	);

	const dateChangeHandler = useCallback(
		(date: DateOnly): void => {
			onNavigate(calendarRouting.page({ queryParams: { date: date.toISOString() }}));
		},
		[onNavigate]
	);

	const createNewEventHandler = useCallback(
		(): void => {
			setComposeAppointmentDialogData({
				isOpen: true,
				initialData: undefined
			});
		},
		[]
	);

	const canNavigateToOwa: boolean = isExchangeEnabled && isExchangeConfigured;

	const navigateToOwaHandler = useCallback(
		(): void => {
			navigateToOwaCalendar(true);
		},
		[]
	);

	useEffect(
		() => {
			const {
				calendar
			} = userConfiguration();

			if (calendar.clientPreference === CalendarClientPreference.UseNone) {
				setHttpError({
					statusCode: 403
				});
			}
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			addAnalyticsAppEvent({
				appType: AppType.Office365Calendar
			});

			return setActiveGlobalNavigationCriteria({
				type: "app",
				appType: AppType.Office365Calendar
			});
		},
		[setActiveGlobalNavigationCriteria, addAnalyticsAppEvent]
	);

	useEffect(
		(): void => {
			// We are not using the "useToday" hook since we don't want any reactivity
			const today: DateOnly = convertToDateOnly(
				new Date(),
				userTimezone
			);

			const hasToday: boolean = dateRangeToRender.toRender
				.some((x) => x.isEqualTo(today));

			let minutesToScrollTo: number;
			if (hasToday) {
				const now: TimeOnly = convertToTimeOnly(new Date(), userTimezone);
				minutesToScrollTo = (now.hour * MINUTES_IN_HOUR) + now.minute;
			} else {
				minutesToScrollTo = DEFAULT_SCROLL_HOUR * MINUTES_IN_HOUR;
			}

			calendarContentRef.current
				?.scrollToMinutes(minutesToScrollTo);
		},
		[dateRangeToRender.toRender, userTimezone]
	);

	const calendarsRef: ReadonlyRefObject<readonly CalendarItem[]> = useSynchronizedRef(calendars);

	const reloadAppointments = useCallback(
		(dateRangeToFetchAppointments: readonly DateOnly[]): void => {
			resetAppointmentLoading(dateRangeToFetchAppointments, userTimezone);

			// Mimic the order of the sidebar
			const calendarsToLoad: readonly CalendarForAppointmentLoading[] = sortBy(
				sortBy(
					calendarsRef.current
						.filter((x): x is EnabledCalendarItem => x.isEnabled),
					(x): number => {
						switch (x.kind) {
							case CalendarKind.Personal:
								return 0;
							case CalendarKind.Shared:
								return 1;
							case CalendarKind.Contact:
								return 2;
							default:
								never(x.kind);
						}
					}
				),
				(x) => x.isDefault ? 0 : 1
			)
				.map((x): CalendarForAppointmentLoading => ({
					id: x.id,
					color: x.color,
					canEdit: x.canEdit
				}));

			if (calendarsToLoad.length === 0) {
				return;
			}

			enqueueAppointmentRetrieval(calendarsToLoad);
		},
		[
			calendarsRef,
			enqueueAppointmentRetrieval,
			resetAppointmentLoading,
			userTimezone
		]
	);

	useEffect(
		(): (() => void) | void => {
			const range: readonly DateOnly[] = dateRangeToFetchAppointments;

			if (isNil(updateInterval) || range.length === 0) {
				return;
			}

			reloadAppointments(dateRangeToFetchAppointments);

			const intervalHandle: SetIntervalHandle = setInterval(
				(): void => reloadAppointments(dateRangeToFetchAppointments),
				updateInterval
			);

			return (): void => clearInterval(intervalHandle);
		},
		[
			calendarsRef,
			dateRangeToFetchAppointments,
			enqueueAppointmentRetrieval,
			resetAppointmentLoading,
			userTimezone,
			updateInterval,
			reloadAppointments
		]
	);

	useEffect(
		(): (() => void) | undefined => {
			const {
				calendar
			} = userConfiguration();

			if (calendar.clientPreference === CalendarClientPreference.UseNone) {
				return;
			}

			async function fetchCalendars(signal: AbortSignal): Promise<void> {
				setLoadingCalendarsState({
					isLoading: true
				});

				try {
					const {
						calendars,
						currentUserWorkingDays,
						liveUpdateInterval,
						defaultView
					} = await getViewModel(signal);

					const result: readonly CalendarItem[] = calendars
						.map(
							(x): CalendarItem => {
								return setClientId({
									...x,
									...(
										isNotNil(x.configuration)
											? {
												isEnabled: true,
												color: x.configuration
													.color
											}
											: {
												isEnabled: false
											}
									)
								});
							}
						);

					const updateIntervalInMs: number = liveUpdateInterval * 1000;

					setView(defaultView);
					setCalendars(result);
					setWorkingDays(currentUserWorkingDays);
					setLoadingCalendarsState({
						isLoading: false,
						hasError: false
					});

					// Setting the updateInterval will also start the appointment retrieval process
					setUpdateInterval(updateIntervalInMs);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					let error: ExchangeConfigurationErrorKind | undefined;
					if (e instanceof HttpRequestError && e.statusCode === 400) {
						try {
							const {
								errorKind
							} = e.readJson<ExchangeConfigurationErrorResponseModel>();

							switch (errorKind) {
								case ExchangeConfigurationErrorKind.ExchangeIsNotConfigured:
								case ExchangeConfigurationErrorKind.AuthenticationError:
									error = errorKind;
									break;
								default:
									console.error(`Unsupported error reason "${errorKind}"`);
									break;
							}
						} catch (e) {
							console.error(e);
						}
					}

					setLoadingCalendarsState({
						isLoading: false,
						hasError: true,
						errorKind: error
					});
				}
			}

			const cancellation = new AbortController();
			fetchCalendars(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, setView]
	);

	const mutateCalendarItem = useCallback(
		(
			calendarId: string,
			mutator: (item: CalendarItem) => CalendarItem
		): void => {
			setCalendars(
				(x): readonly CalendarItem[] => x.map(
					(c): CalendarItem => {
						if (c.id === calendarId) {
							return mutator(c);
						}

						return c;
					}
				)
			);
		},
		[]
	);

	const enableCalendarAndFetchAppointments = useCallback(
		(
			calendar: CalendarItem,
			color: CalendarColor,
			signal?: AbortSignal
		): void => {
			const { id, canEdit } = calendar;

			(async (): Promise<void> => {
				const result: CalendarSaveResultModel = await enableCalendar(
					{
						color,
						id
					},
					signal
				);

				mutateCalendarItem(
					id,
					(x): CalendarItem => ({
						...x,
						configuration: {
							id: result.id,
							color
						}
					})
				);
			})();

			enqueueAppointmentRetrieval([{
				id,
				color,
				canEdit
			}]);
		},
		[enqueueAppointmentRetrieval, mutateCalendarItem]
	);

	const toggleCalendarHandler = useCallback(
		(
			id: string,
			isEnabled: boolean,
			signal: AbortSignal
		): void => {
			const selectedCalendar: CalendarItem | undefined = calendars.find(
				x => x.id === id
			);

			if (isNil(selectedCalendar)) {
				return;
			}

			const calendarId: string = selectedCalendar.id;

			if (isEnabled) {
				const colorsInUse: readonly CalendarColor[] = calendars
					.filter((x): x is EnabledCalendarItem => x.isEnabled)
					.map(x => x.color);

				const color: CalendarColor = isMainCalendar(selectedCalendar)
					? DEFAULT_MAIN_CALENDAR_COLOR
					: getNewCalendarColor(colorsInUse);

				mutateCalendarItem(
					calendarId,
					(x): CalendarItem => ({
						...x,
						isEnabled: true,
						color
					})
				);

				enableCalendarAndFetchAppointments(
					selectedCalendar,
					color,
					signal
				);
			} else {
				cancelAppointmentRetrieval(calendarId);

				if (isNotNil(selectedCalendar.configuration)) {
					disableCalendar(
						selectedCalendar.configuration
							.id
					);
				}

				mutateCalendarItem(
					calendarId,
					(x): CalendarItem => ({
						...x,
						configuration: null,
						isEnabled: false
					})
				);

				setAppointments(
					(x): readonly CalendarAppointment[] => {
						return x.filter(
							a => a.kind === "regular" && a.calendarId !== calendarId
						);
					}
				);
			}
		},
		[
			calendars,
			cancelAppointmentRetrieval,
			mutateCalendarItem,
			enableCalendarAndFetchAppointments
		]
	);

	const composeAppointmentBoxEnlargeHandler = useCallback(
		(initialData: CalendarAppointmentFormModel): void => {
			setComposeAppointmentBoxLocationAndData(null);

			setComposeAppointmentDialogData({
				isOpen: true,
				initialData
			});
		},
		[]
	);

	const afterMutatingAppointmentHandler = useCallback(
		(calendarIds: readonly string[]): void => {
			const calendarsToReloadOrEnable: CalendarItem[] = calendars.filter(
				(x: CalendarItem): boolean => calendarIds.includes(x.id)
			);

			if (calendarsToReloadOrEnable.length === 0) {
				console.error("calendars not found");
				return;
			}

			const [enabledCalendars, disabledCalendars] = partition(
				calendarsToReloadOrEnable,
				x => x.isEnabled
			);

			disabledCalendars.forEach((x: DisabledCalendarItem): void => {
				toggleCalendarHandler(
					x.id,
					true,
					globalCancellation
				);
			});

			if (enabledCalendars.length > 0) {
				enabledCalendars.forEach((x: EnabledCalendarItem): void => {
					cancelAppointmentRetrieval(x.id);
				});

				enqueueAppointmentRetrieval(
					enabledCalendars.map(
						(x: EnabledCalendarItem): CalendarForAppointmentLoading => {
							return {
								id: x.id,
								color: x.color,
								canEdit: x.canEdit
							};
						}
					)
				);
			}
		},
		[
			calendars,
			cancelAppointmentRetrieval,
			enqueueAppointmentRetrieval,
			globalCancellation,
			toggleCalendarHandler
		]
	);

	const closeComposeAppointmentDialogHandler = useCallback(
		(calendarToRefreshIds?: readonly string[]): void => {
			setComposeAppointmentDialogData({ isOpen: false });

			if (isNil(calendarToRefreshIds)) {
				return;
			}

			afterMutatingAppointmentHandler(calendarToRefreshIds);

		},
		[afterMutatingAppointmentHandler]
	);

	const closeComposeAppointmentBoxHandler = useCallback(
		(calendarToRefreshIds?: readonly string[]): void => {
			setComposeAppointmentBoxLocationAndData(null);

			if (isNil(calendarToRefreshIds)) {
				return;
			}

			afterMutatingAppointmentHandler(calendarToRefreshIds);
		},
		[afterMutatingAppointmentHandler]
	);

	const openCreateAppointmentBoxHandler = useCallback(
		(
			coordinate: Coordinate,
			date: DateOnly,
			time?: TimeOnly
		): void => {
			executeAfterDropoverCloses(
				(): void => {
					const day: Date = convertDateOnlyToDateTime(date, userTimezone);
					const isAllDay: boolean = isNil(time);
					const startTime: Date = isNotNil(time)
						? convertToDateTime(time, userTimezone, day)
						: day;

					setComposeAppointmentBoxLocationAndData({
						coordinate,
						data: {
							isAllDay,
							startTime
						}
					});
				}
			);
		},
		[userTimezone]
	);

	const openEditAppointmentBoxHandler = useCallback(
		(
			coordinate: Coordinate,
			data: EditAppointmentInfo
		): void => {
			executeAfterDropoverCloses(
				(): void => {
					const { appointment, recurringOperationKind } = data;

					setComposeAppointmentBoxLocationAndData({
						coordinate,
						data: {
							...appointment,
							recurringOperationKind
						}
					});
				}
			);
		},
		[]
	);

	const deleteContactCalendarHandler = useCallback(
		async (id: string): Promise<void> => {
			const result = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToRemoveThisCalendar"),
				confirmText: localize("_Remove"),
				trackingId: "remove-calendar-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			try {
				setIsDeletingCalendar(true);
				await deleteContactCalendar(
					id,
					globalCancellation
				);

				alertManager.showSuccess(localize("_SuccessfullyRemovedTheCalendar"));

				setCalendars(calendars => {
					return calendars.filter(
						x => x.id !== id
					);
				});

				setAppointments(
					(x): readonly CalendarAppointment[] => {
						return x.filter(
							a => a.kind === "regular" && a.calendarId !== id
						);
					}
				);

				setIsDeletingCalendar(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsDeletingCalendar(false);
				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}

		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const addCalendarHandler = useCallback(
		async (): Promise<void> => {
			const { current: calendars } = calendarsRef;
			const colorsInUse: readonly CalendarColor[] = calendars
				.filter((x): x is EnabledCalendarItem => x.isEnabled)
				.map(x => x.color);

			const color: CalendarColor = getNewCalendarColor(colorsInUse);

			const result: TryResult<CalendarInfoModel> = await openAddCalendarDialog({
				calendarColor: color
			});

			if (!result.wasSuccessful) {
				return;
			}

			const newCalendar: CalendarItem = setClientId({
				...result.result,
				isEnabled: true,
				color
			});

			setCalendars(x => ([
				...x,
				newCalendar
			]));

			enableCalendarAndFetchAppointments(
				newCalendar,
				color
			);
		},
		[
			calendarsRef,
			enableCalendarAndFetchAppointments,
			openAddCalendarDialog
		]
	);

	const appointmentPlaceholder = useMemo(
		(): CalendarAppointment | null => {
			if (
				isNotNil(composeAppointmentBoxLocationAndData)
					&& isNil(
						composeAppointmentBoxLocationAndData.data
							?.id
					)
			) {
				const start: Date = ensureNotNil(
					composeAppointmentBoxLocationAndData.data
						.startTime
				);

				const end: Date = getNextHalfOrWholeHour(
					start,
					userTimezone
				);

				const placeholder: PlaceholderCalendarAppointment = {
					kind: "placeholder",
					start,
					end,
					isAllDayEvent: composeAppointmentBoxLocationAndData
						.data
						.isAllDay,
					color: DEFAULT_MAIN_CALENDAR_COLOR
				};

				return setClientId(placeholder);
			}

			return null;
		},
		[composeAppointmentBoxLocationAndData, userTimezone]
	);

	const computedAppointments = useMemo(
		(): readonly CalendarAppointment[] => {
			if (isNil(appointmentPlaceholder)) {
				return appointments;
			}

			return [
				...appointments,
				appointmentPlaceholder
			];
		},
		[appointmentPlaceholder, appointments]
	);

	const isCalendarContentBusy = useMemo(
		(): boolean => {
			if (loadingStatusMap.size === 0) {
				return false;
			}

			let isStillLoading: boolean = false;
			for (const status of loadingStatusMap.values()) {
				if (status === "success") {
					return false;
				}

				if (status === "loading") {
					isStillLoading = true;
				}
			}

			return isStillLoading;
		},
		[loadingStatusMap]
	);

	const renderErrorDialogs = (): ReactElement | null => {
		if (loadingCalendarsState.isLoading || loadingCalendarsState.hasError === false) {
			return null;
		}

		switch(loadingCalendarsState.errorKind) {
			case ExchangeConfigurationErrorKind.AuthenticationError:
				return <ExchangeAuthenticationErrorDialog />;
			case ExchangeConfigurationErrorKind.ExchangeIsNotConfigured:
				return (
					<EmailNotConfiguredDialog
						shouldNavigateBackToWorkspaceOnClose
						hideConfigurationSuggestion={!isExchangeEnabled}
					/>
				);
			default:
				return <PageFailedDialog />;
		}
	};

	const hasSidebarBusyIndicator: boolean = loadingCalendarsState.isLoading
		|| isDeletingCalendar;

	return (
		<>
			<CalendarSidebar
				isLoading={hasSidebarBusyIndicator}
			>
				{(onSidebarNavigation) => (
					<CalendarSidebarContent
						date={selectedDate}
						calculateHighlightRangeForDate={calculateHighlightRangeForDate}
						calendars={calendars}
						onDateChange={dateChangeHandler}
						onSidebarNavigation={onSidebarNavigation}
						onIsEnabledChange={toggleCalendarHandler}
						loadingStatusMap={loadingStatusMap}
						timezone={userTimezone}
						onAddCalendar={addCalendarHandler}
						onDeleteContactCalendar={deleteContactCalendarHandler}
					/>
				)}
			</CalendarSidebar>

			<div
				className={styles.root}
				ref={pageRef}
			>
				<CalendarContent
					isBusy={isCalendarContentBusy}
					dateRange={dateRangeToRender}
					appointments={computedAppointments}
					onNewEvent={createNewEventHandler}
					onDateChange={dateChangeHandler}
					onViewChange={setView}
					isMobile={isMobile}
					isResponsive={isResponsive}
					ref={calendarContentRef}
					timezone={userTimezone}
					view={viewToRender}
					calculateHighlightRangeForDate={calculateHighlightRangeForDate}
					onNavigateToOwa={canNavigateToOwa ? navigateToOwaHandler : undefined}
					onCreateAppointment={openCreateAppointmentBoxHandler}
					onEditAppointment={openEditAppointmentBoxHandler}
					onAfterMutatingAppointment={afterMutatingAppointmentHandler}
					editingAppointmentId={composeAppointmentBoxLocationAndData?.data
						.id
					}
				/>
			</div>
			{renderErrorDialogs()}
			<FloatingActionButton
				icon="plus"
				trackingId="new-appointment"
				onClick={createNewEventHandler}
			/>
			{!loadingCalendarsState.isLoading && composeAppointmentDialogData.isOpen && (
				<ComposeAppointmentDialog
					onClose={closeComposeAppointmentDialogHandler}
					timezone={userTimezone}
					initialData={composeAppointmentDialogData.initialData}
				/>
			)}
			{!loadingCalendarsState.isLoading
				&& composeAppointmentBoxLocationAndData
				&& isNotNil(pageRef.current) && (
					<ComposeAppointmentBox
						trackingId="create-appointment-info-box"
						onClose={closeComposeAppointmentBoxHandler}
						onEnlarge={composeAppointmentBoxEnlargeHandler}
						target={pageRef.current}
						coordinate={composeAppointmentBoxLocationAndData.coordinate}
						timezone={userTimezone}
						calendars={calendars}
						initialData={composeAppointmentBoxLocationAndData.data}
					/>
				)
			}
			{addCalendarDialog}
			{confirmationDialog}
		</>
	);
};
CalendarPage.displayName = "CalendarPage";

type LoadingCalendarsState = {
	isLoading: true;
} | {
	isLoading: false;
	hasError: false;
} | {
	isLoading: false;
	hasError: true;
	errorKind?: ExchangeConfigurationErrorKind;
};

export const CalendarPageForRouter = connectToRouter(
	CalendarPage,
	{
		"date": {
			isRequired: false,
			tryParse: tryParseDateOnly,
			type: "query",
			shouldIgnoreInvalidValues: true
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export type CalendarPageQueryParams = QueryParametersFromProps<
	CalendarPageProps,
	"date"
>;

export const calendarRouting = {
	page: route<undefined, CalendarPageQueryParams>`/Calendar`
};

const CALENDAR_DEFAULT_VIEW: CalendarContentViewKind = CalendarContentViewKind.Week;

export function getDayCountOfViewKind(
	viewKind: CalendarContentViewKind
): number {
	switch(viewKind) {
		case CalendarContentViewKind.Day:
			return 1;
		case CalendarContentViewKind.ThreeDays:
			return 3;
		case CalendarContentViewKind.FiveDays:
			return 5;
		case CalendarContentViewKind.Week:
		case CalendarContentViewKind.WorkWeek:
			return 7;
		default:
			never(viewKind);
	}
}

const UNSET_WORKING_DAYS: WorkingDaysModel = {
	monday: false,
	tuesday: false,
	wednesday: false,
	thursday: false,
	friday: false,
	saturday: false,
	sunday: false
};

export type RegularCalendarAppointment = {
	kind: "regular";
	id: string | null;
	masterId: string | null;
	subject: string | null;
	start: Date;
	end: Date;
	isAllDayEvent: boolean;
	locations: readonly string[] | null;
	meeting: OnlineMeeting | null;
	isRecurring: boolean;
	hasAttachments: boolean;
	isPrivate: boolean;
	isCancelled: boolean;
	color: CalendarColor | null;
	calendarId: string;
	myResponseStatus: AppointmentResponseStatus | null;
	isOrganizer: boolean;
	canEdit: boolean;
};

export type PlaceholderCalendarAppointment = {
	kind: "placeholder";
	start: Date;
	end: Date;
	isAllDayEvent: boolean;
	color: CalendarColor | null;
};

export type CalendarAppointment = ObjectWithClientId<RegularCalendarAppointment | PlaceholderCalendarAppointment>;

function getNewCalendarColor(selectedColors: readonly CalendarColor[]): CalendarColor {
	const allCalendarColors: readonly CalendarColor[] = getNumericEnumValues(CalendarColor)
		.filter(x => x !== CalendarColor.Info);

	const availableColors: readonly CalendarColor[] = allCalendarColors.filter(
		x => !selectedColors.includes(x)
	);

	if (availableColors.length === 0) {
		// If all colors have been selected, return the next color in the original sequence
		const index: number = selectedColors.length % allCalendarColors.length;
		return selectedColors[index];
	}

	const randomIndex: number = Math.floor(Math.random() * availableColors.length);

	return availableColors[randomIndex];
}

const DEFAULT_MAIN_CALENDAR_COLOR = CalendarColor.Info;


const DEFAULT_SCROLL_HOUR: number = 8;

function isMainCalendar(calendar: CalendarItem): boolean {
	return calendar.kind === CalendarKind.Personal
		&& calendar.isDefault;
}

export type CalendarDateRange = {
	value: DateOnly;
	toRender: readonly DateOnly[];
};

type ComposeAppointmentDialogInfoData = {
	isOpen: false;
} | {
	isOpen: true;
	initialData?: CalendarAppointmentFormModel;
};

export type ComposeAppointmentBoxLocationAndData = {
	coordinate: Coordinate;
	data: ComposeAppointmentBoxInitialData;
};

export type EditAppointmentInfo = {
	appointment: AppointmentDetailModel;
	recurringOperationKind?: RecurringAppointmentOperationKind;
};

function executeAfterDropoverCloses(callback: () => void): void {
	// The setTimeout ensures that a new dropover opens only after the previous one has closed.
	// This handles the scenario where the user tries to open a new dropover while another one is still open.
	setTimeout(callback, 0);
};